// components/chakra/NavbarWithCallToAction/NavbarWithCallToAction.tsx

import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  IconButton,
  Link,
  Stack,
  useBreakpointValue,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
  useColorModeValue,
  Avatar,
  VStack,
} from "@chakra-ui/react";
import { Logo } from "../../../Logo";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { CloseIcon, HamburgerIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { SecureMembershipButton, LoginWithSmsModal } from "../../../pages/authentication";
import { getColor, getComponent } from "../../../brandConfig";

// Import module configs for dynamic navigation
import profileModuleConfig from "../../../pages/profile/moduleConfig";
import subscriptionModuleConfig from "../../../pages/subscriptions/moduleConfig";
import clientsModuleConfig from "../../../pages/clients/moduleConfig";
import employeesModuleConfig from "../../../pages/employees/moduleConfig";
import companiesModuleConfig from "../../../pages/companies/moduleConfig";
import assetsModuleConfig from "../../../pages/assets/moduleConfig";
import { calendarsModuleConfig } from "../../../pages/calendars/moduleConfig";
import sessionsModuleConfig from "../../../pages/sessions/moduleConfig";
import productsModuleConfig from "../../../pages/products/moduleConfig";
import projectsModuleConfig from "../../../pages/projects/moduleConfig";
import billsModuleConfig from "../../../pages/bills/moduleConfig";
import emailsModuleConfig from "../../../pages/emails/moduleConfig";
import passwordsModuleConfig from "../../../pages/passwords/moduleConfig";
import researchAndDesignModuleConfig from "../../../pages/researchanddesign/moduleConfig";
import adminModuleConfig from "../../../pages/admin/moduleConfig";
import hamishModuleConfig from "../../../pages/hamish/moduleConfig";
import { phoneSystemModuleConfig } from "../../../pages/phone-system/moduleConfig";
import { knowledgebaseModuleConfig } from "../../../pages/knowledgebase/moduleConfig";
import { vapiModuleConfig } from "../../../pages/vapi/moduleConfig";

interface NavLink {
  name: string;
  path: string;
  category?: string;
}

// Organized navigation links by module (matching business plan structure)
const NavigationLinks = {
  // core: [
  //   { name: "Home", path: "/" },
  //   { name: "Services", path: "/" },
  //   { name: "Intro Session", path: "/intro-session" },
  // ],
  // clients: [
  //   { name: "All Clients", path: "/clients" },
  //   { name: "New Client", path: "/newclient" },
  // ],
  // manualCharging: [
  //   { name: "New Client", path: "/newclient" },
  //   { name: "Payment Methods", path: "/subscriptions/payment-methods" },
  //   { name: "Manual Charging", path: "/subscriptions/manual-charging" },
  // ],
  // subscriptions: [
  //   { name: "All Subscriptions", path: "/subscriptions" },
  //   { name: "Manage Subscriptions", path: "/subscriptions/manage" },
  //   { name: "Create Subscription", path: "/subscriptions/create" },
  //   { name: "Invoices", path: "/subscriptions/invoices" },
  //   { name: "Payment Methods", path: "/subscriptions/payment-methods" },
  //   { name: "Manual Charging", path: "/subscriptions/manual-charging" },
  //   { name: "Settings", path: "/subscriptions/settings" },
  // ],
  // projects: [
  //   { name: "All Projects", path: "/projects" },
  //   { name: "New Project", path: "/projects/new" },
  //   { name: "Project Timeline", path: "/projects/timeline" },
  // ],
  // products: [
  //   { name: "All Products", path: "/products" },
  //   { name: "New Product", path: "/products/new" },
  //   { name: "Shopping Cart", path: "/cart" },
  //   { name: "Print Labels", path: "/print-labels" },
  // ],
  // sellerprofile: [
  //   { name: "All Seller Profiles", path: "/seller-profiles" },
  //   { name: "New Seller Profile", path: "/seller-profiles/new" },
  // ],
  // sessions: [
  //   { name: "My Bookings", path: "/sessions/client-booking-calendar" },
  //   { name: "Session Types", path: "/sessions/types" },
  //   { name: "Create Session Type", path: "/sessions/types/create" },
  //   { name: "Set Availability", path: "/sessions/set-availabilities" },
  //   { name: "Schedule Session", path: "/schedule-session" },
  // ],
  // simpleBills: [
  //   { name: "All Bills", path: "/bills" },
  //   { name: "New Bill", path: "/bills/new" },
  // ],
  // admin: [
  //   { name: "Admin Dashboard", path: "/admin" },
  //   { name: "Tenant Management", path: "/admin/tenants" },
  //   { name: "Create Tenant", path: "/admin/tenants/new" },
  //   { name: "Deployment Manager", path: "/admin/deploy" },
  //   { name: "Module Deployment", path: "/admin/module-deployment" },
  //   { name: "Tenant Deployment Manager", path: "/admin/tenant-deployment" },
  //   { name: "Bill Chaser", path: "/bill-chaser" },
  // ],
  // pages: [
  //   { name: "About Us", path: "/about" },
  //   { name: "Founders", path: "/founders" },
  //   { name: "Governance", path: "/governance" },
  //   { name: "Blog", path: "/blog" },
  //   { name: "News", path: "/news" },
  //   { name: "Agreement", path: "/agreement" },
  //   { name: "Welcome", path: "/welcome" },
  //   { name: "100 Members", path: "/100members" },
  //   { name: "Ecosystem Agreement", path: "/ecosystem-agreement" },
  // ],
};

export const NavbarWithCallToAction = () => {
  // Changed from 3xl to lg for better compatibility
  const isDesktop = useBreakpointValue({ base: false, lg: true });
  const { isOpen, onToggle } = useDisclosure();
  const { isOpen: isLoginOpen, onOpen: onLoginOpen, onClose: onLoginClose } = useDisclosure();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, refreshAuth } = useAuth();

  // All available module configs
  const allModuleConfigs = [
    profileModuleConfig,
    clientsModuleConfig,
    employeesModuleConfig,
    companiesModuleConfig,
    assetsModuleConfig,
    calendarsModuleConfig,
    passwordsModuleConfig,
    sessionsModuleConfig,
    productsModuleConfig,
    projectsModuleConfig,
    billsModuleConfig,
    emailsModuleConfig,
    phoneSystemModuleConfig,
    vapiModuleConfig,
    researchAndDesignModuleConfig,
    subscriptionModuleConfig,
    knowledgebaseModuleConfig,
    adminModuleConfig,
  ];

  // Filter modules based on user permissions and enabled status
  const getAvailableModules = (enabledModules: string[] = []) => {
    return allModuleConfigs.filter(module => {
      // Check if module is enabled (if enabledModules is provided, otherwise show all)
      const isEnabled = enabledModules.length === 0 || enabledModules.includes(module.id);
      
      // Check if user has view permission for this module
      const hasPermission = user?.permissions?.some((permission: string) => {
        // Check various permission formats
        if ('view' in module.permissions) {
          return module.permissions.view.includes(permission);
        } else if ('viewer' in module.permissions) {
          return module.permissions.viewer.includes(permission) || 
                 module.permissions.manager?.includes(permission) || 
                 module.permissions.admin?.includes(permission);
        }
        return false;
      }) || false;
      
      return isEnabled && hasPermission;
    });
  };

  // Filter navigation items based on user permissions
  const getVisibleNavItems = (navItems: any[]) => {
    return navItems.filter((item: any) => {
      if (!item.permissions || item.permissions.length === 0) return true;
      
      return user?.permissions?.some((permission: string) => 
        item.permissions.includes(permission)
      ) || false;
    });
  };

  // Color mode values for dropdown styling
  const menuBorderColor = useColorModeValue('gray.200', 'gray.600');
  const menuItemHoverBg = useColorModeValue('gray.50', 'gray.700');

  const handleAuthAction = async () => {
    if (isAuthenticated) {
      await logout();
      navigate("/");
    } else {
      onLoginOpen(); // Open login modal instead of navigating
    }
  };

  const handleLoginSuccess = () => {
    onLoginClose();
    refreshAuth(); // Refresh auth context after successful login
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <Box
        position="sticky"
        top={0}
        left={0}
        right={0}
        zIndex="sticky"
        bg="transparent"
        backdropFilter="blur(10px)"
        borderBottom="1px solid"
        borderColor="rgba(255, 255, 255, 0.1)"
        boxShadow="0 4px 20px 0 rgba(0, 0, 0, 0.05)"
        margin={0}
        padding={0}
      >
        <Container maxW="7xl" position="relative" zIndex={1} px={{ base: 4, md: 6 }}>
          <Flex
            minH={"64px"}
            py={{ base: 3, md: 4 }}
            align={"center"}
            justify={"space-between"}
            gap={{ base: 2, md: 4 }}
          >
            <Link onClick={handleLogoClick} style={{ cursor: "pointer" }}>
              <Logo />
            </Link>

          <HStack spacing={{ base: 2, md: 4 }}>
            {/* Podcast Button - Always visible */}
            <Button
              variant="ghost"
              size="md"
              onClick={() => navigate("/podcast")}
              color={getColor("primary")}
              fontWeight="600"
              _hover={{
                color: getColor("secondary"),
                bg: getColor("background.overlay"),
                transform: "translateY(-2px)",
              }}
              transition="all 0.2s ease"
            >
              Podcast
            </Button>
            
            {isAuthenticated ? (
              /* Combined Navigation + Profile Menu - COMMENTED OUT */
              null
              /* <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<HamburgerIcon />}
                  variant="ghost"
                  color={getColor("primary")}
                  aria-label="Open menu"
                  size="md"
                  rounded="full"
                  transition="all 0.2s ease"
                  _hover={{
                    color: getColor("secondary"),
                    bg: getColor("background.overlay"),
                    transform: "scale(1.05)",
                  }}
                />
                <MenuList
                  border="1px solid"
                  borderColor={menuBorderColor}
                  shadow="xl"
                  rounded="xl"
                  py={2}
                  minW="320px"
                  maxH="500px"
                  overflowY="auto"
                >
                  {/* User Profile Section *//*}
                  {user && (
                    <>
                      <Box px={3} py={2}>
                        <HStack spacing={3}>
                          <Avatar
                            size="sm"
                            name={`${user.fName || ""} ${user.lName || ""}`}
                            src={user.profilePhoto || undefined}
                            bg={getColor("primary")}
                          />
                          <VStack align="start" spacing="1px">
                            <Text fontSize="sm" fontWeight="semibold">
                              {user.fName} {user.lName}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {user.email}
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>
                      <MenuDivider />
                    </>
                  )}

                  {/* Navigation Sections *//*}

                  {/* Dynamic Modules Section *//*}
                  {(() => {
                    const availableModules = getAvailableModules();
                    if (availableModules.length === 0) return null;
                    
                    return (
                      <>
                        <Text fontSize="xs" fontWeight="bold" color="gray.500" px={3} py={1}>
                          📦 MODULES
                        </Text>
                        {availableModules
                          .sort((a, b) => {
                            const aOrder = 'order' in a ? a.order : 0;
                            const bOrder = 'order' in b ? b.order : 0;
                            return aOrder - bOrder;
                          })
                          .map(module => {
                            // Handle both array and object navigation formats
                            const navArray = Array.isArray(module.navigation) 
                              ? module.navigation 
                              : module.navigation.sub || [];
                            const visibleNavItems = getVisibleNavItems(navArray);
                            if (visibleNavItems.length === 0) return null;
                            
                            return (
                              <Box key={module.id}>
                                {/* Module Name as Subheader *//*}
                                <Text 
                                  fontSize="xs" 
                                  fontWeight="medium" 
                                  color="gray.400" 
                                  px={4} 
                                  py={1}
                                  textTransform="uppercase"
                                  letterSpacing="wide"
                                >
                                  {module.name}
                                </Text>
                                
                                {/* Module Navigation Items *//*}
                                {visibleNavItems.map(navItem => (
                                  <MenuItem
                                    key={navItem.path}
                                    onClick={() => handleNavigation(navItem.path)}
                                    rounded="md"
                                    mx={2}
                                    py={2}
                                    fontSize="sm"
                                    _hover={{ bg: menuItemHoverBg }}
                                    pl={6} // Extra indentation for module items
                                  >
                                    {navItem.icon && (
                                      <Text as="span" mr={2} fontSize="sm">
                                        {navItem.icon}
                                      </Text>
                                    )}
                                    {navItem.label}
                                  </MenuItem>
                                ))}
                              </Box>
                            );
                          })}
                        <MenuDivider />
                      </>
                    );
                  })()}

                 

      

                
                  <MenuItem
                    onClick={handleLogout}
                    rounded="md"
                    mx={2}
                    py={2}
                    fontSize="sm"
                    color="red.500"
                    _hover={{
                      bg: "red.50",
                      color: "red.600",
                    }}
                  >
                    Sign out
                  </MenuItem>
                </MenuList>
              </Menu> */
            ) : (
              <SecureMembershipButton />
            )}

            {/* Mobile navigation removed since we have dropdown menu */}
          </HStack>

          {/* Auth actions are now in the hamburger menu */}
        </Flex>

        {/* Mobile menu removed - using dropdown menu for all screen sizes */}
      </Container>

      {/* SMS Login Modal */}
      <LoginWithSmsModal
        isOpen={isLoginOpen}
        onClose={onLoginClose}
        onLoginSuccess={handleLoginSuccess}
      />
    </Box>
  );
};